import type { StateStore } from '@playcastdotio/CoreTypes';
import { generators } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';
import type { IIpcService } from '../../services/ipc/IIpcService';
import type { IRewardsService } from '../../services/rewards/IRewardsService';
import { machineIdState } from '../../api/stores/shared';
import { serviceContainer } from '../../services/ServiceContainer';
import { createRestartStore, type RestartState } from './restartStore';

const STORAGE_KEY = 'playcast_scheduled_reboot_time';
const REBOOT_REWARD_KEY = 'pendingRebootReward';

export interface RestartManagerDeps {
  ipcService: IIpcService;
  rewardsService: IRewardsService;
}

/**
 * RestartManager — single owner of restart-schedule state.
 *
 * Responsibilities:
 * - Persist/read scheduled reboot time in localStorage
 * - Send schedule/cancel/reboot IPC and handle replies
 * - Roll back optimistic state on failure
 *
 * Pattern: Single-responsibility manager (ARCHITECTURE.md §3)
 */
export class RestartManager {
  private store: StateStore<RestartState>;
  private deps: RestartManagerDeps;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private _pendingCancelEpoch = 0;
  private _pendingRebootEpoch = 0;
  private _requestGen = 0;
  private _scheduleGen = 0;
  private _cancelGen = 0;
  private _rebootGen = 0;

  constructor(deps: RestartManagerDeps) {
    this.deps = deps;
    this.store = createRestartStore();
  }

  public getStore(): StateStore<RestartState> {
    return this.store;
  }

  public init(): void {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const epochSec = Number(stored);
      const targetMs = epochSec * 1000;
      if (targetMs <= Date.now()) {
        console.log('[RestartManager] init: found expired time in localStorage, clearing:', stored);
        localStorage.removeItem(STORAGE_KEY);
      } else {
        console.log('[RestartManager] init: hydrating from localStorage — epochSec=%d, remainingMs=%d', epochSec, targetMs - Date.now());
        this.store.updateState({ scheduled: true, scheduledTimeUnix: epochSec, lastError: '' });
        this.startExpiryTimer(targetMs - Date.now());
      }
    } else {
      console.log('[RestartManager] init: no stored schedule found in localStorage');
    }
    console.log('[RestartManager] Initialized, store:', this.store.currentState);
  }

  public scheduleReboot(epochSec: number): void {
    console.log('[RestartManager] scheduleReboot called — epochSec=%d', epochSec);
    this._requestGen++;
    this._scheduleGen = this._requestGen;
    localStorage.setItem(STORAGE_KEY, String(epochSec));
    this.store.updateState({ scheduled: true, scheduledTimeUnix: epochSec, lastError: '' });
    console.log('[RestartManager] scheduleReboot: store updated, scheduled=true, gen=%d', this._requestGen);

    const remaining = (epochSec * 1000) - Date.now();
    this.startExpiryTimer(remaining > 0 ? remaining : 0);

    serviceContainer.analyticsManager.sendRestartScheduleCreatedEvent({
      scheduledTime: new Date(epochSec * 1000).toISOString(),
      delayMinutes: Math.round(remaining / 60_000),
    });

    this.deps.ipcService.send(generators.system.scheduleReboot(epochSec));
  }

  public cancelSchedule(): void {
    this._requestGen++;
    this._cancelGen = this._requestGen;
    this.clearExpiryTimer();
    const previousEpoch = this.store.currentState.scheduledTimeUnix;
    localStorage.removeItem(STORAGE_KEY);
    this.store.updateState({ scheduled: false, scheduledTimeUnix: 0, lastError: '' });
    this.deps.ipcService.send(generators.system.cancelScheduledReboot());
    this._pendingCancelEpoch = previousEpoch;
    serviceContainer.analyticsManager.sendRestartScheduleCancelledEvent();
  }

  public restartNow(source: 'prompt' | 'confirm' | 'schedule' = 'prompt'): void {
    this._requestGen++;
    this._rebootGen = this._requestGen;
    this._pendingRebootEpoch = this.store.currentState.scheduledTimeUnix;
    this.clearExpiryTimer();
    localStorage.removeItem(STORAGE_KEY);
    this.store.updateState({ scheduled: false, scheduledTimeUnix: 0, lastError: '' });
    serviceContainer.analyticsManager.sendRestartNowClickedEvent({ source });
    this.deps.ipcService.send(
      generators.system.reboot('local', { timeOutLength: 5, forceAppsClosed: true, userInitiated: true }),
    );
  }

  /**
   * Called by playjectorMessageRouter when a reply arrives.
   * Each action branch compares against the generation captured at send time
   * so late replies from superseded requests are silently dropped.
   */
  public handleReply(action: string, message: Record<string, unknown>): void {
    if (action === 'reboot') {
      if (this._requestGen !== this._rebootGen) return;
      const errorCode = (message as { errorCode?: string }).errorCode;
      if (errorCode === 'Reboot initiated...') {
        console.log('[RestartManager] Reboot initiated');
        this._pendingRebootEpoch = 0;
        localStorage.setItem(REBOOT_REWARD_KEY, JSON.stringify({ timestamp: Date.now() }));
      } else {
        console.error('[RestartManager] Reboot failed: %s', errorCode ?? 'unknown');
        localStorage.removeItem(REBOOT_REWARD_KEY);
        const epochToRestore = this._pendingRebootEpoch;
        this._pendingRebootEpoch = 0;
        if (epochToRestore) {
          localStorage.setItem(STORAGE_KEY, String(epochToRestore));
          this.store.updateState({
            scheduled: true,
            scheduledTimeUnix: epochToRestore,
            lastError: String(errorCode || 'Reboot failed'),
          });
          const remaining = (epochToRestore * 1000) - Date.now();
          this.startExpiryTimer(remaining > 0 ? remaining : 0);
        } else {
          this.store.updateState({ ...this.store.currentState, lastError: String(errorCode || 'Reboot failed') });
        }
      }
      return;
    }

    if (action === 'scheduleReboot') {
      if (this._requestGen !== this._scheduleGen) return;
      if (message.success) {
        console.log('[RestartManager] Schedule confirmed by PlaycastService');
      } else {
        console.error('[RestartManager] Schedule FAILED — rolling back');
        this.clearExpiryTimer();
        localStorage.removeItem(STORAGE_KEY);
        this.store.updateState({
          scheduled: false,
          scheduledTimeUnix: 0,
          lastError: String(message.errorCode || 'Failed to create scheduled task'),
        });
      }
      return;
    }

    if (action === 'cancelScheduledReboot') {
      if (this._requestGen !== this._cancelGen) return;
      if (message.success) {
        console.log('[RestartManager] Cancel confirmed');
        this._pendingCancelEpoch = 0;
      } else {
        console.error('[RestartManager] Cancel FAILED — restoring schedule state');
        const epochToRestore = this._pendingCancelEpoch;
        this._pendingCancelEpoch = 0;
        if (epochToRestore) {
          localStorage.setItem(STORAGE_KEY, String(epochToRestore));
          this.store.updateState({
            scheduled: true,
            scheduledTimeUnix: epochToRestore,
            lastError: 'Failed to cancel scheduled task',
          });
          const remaining = (epochToRestore * 1000) - Date.now();
          this.startExpiryTimer(remaining > 0 ? remaining : 0);
        } else {
          this.store.updateState({
            ...this.store.currentState,
            lastError: 'Failed to cancel scheduled task',
          });
        }
      }
      return;
    }
  }

  public destroy(): void {
    this.clearExpiryTimer();
  }

  /**
   * Called after auth + attestation are available. Checks if a "Reboot Now"
   * flag was set before the last reboot and fires the ManualReboot reward action.
   */
  public async checkPendingRebootReward(): Promise<void> {
    const raw = localStorage.getItem(REBOOT_REWARD_KEY);
    if (!raw) return;

    let machineId = machineIdState.currentState;
    if (!machineId) {
      machineId = await new Promise<string>((resolve) => {
        let listenerId: string | undefined;
        const timeout = setTimeout(() => {
          if (listenerId) machineIdState.removeListener(listenerId);
          resolve('');
        }, 10_000);
        listenerId = machineIdState.addListener((id) => {
          if (id) {
            clearTimeout(timeout);
            if (listenerId) machineIdState.removeListener(listenerId);
            resolve(id);
          }
        });
      });
    }

    if (!machineId) {
      console.warn('[RestartManager] checkPendingRebootReward: machineId never arrived, will retry next launch');
      return;
    }

    try {
      const result = await this.deps.rewardsService.fireAction('ManualReboot', machineId, {
        version: constants.VERSION,
      });
      localStorage.removeItem(REBOOT_REWARD_KEY);
      console.log('[RestartManager] ManualReboot reward fired:', result);
      serviceContainer.analyticsManager.sendPostRebootRewardFiredEvent({
        version: constants.VERSION,
        machineId,
      });
    } catch (err) {
      console.error('[RestartManager] Failed to fire ManualReboot reward — key preserved for retry on next launch:', err);
    }
  }

  private startExpiryTimer(ms: number): void {
    this.clearExpiryTimer();
    if (ms <= 0) {
      localStorage.removeItem(STORAGE_KEY);
      this.store.updateState({ scheduled: false, scheduledTimeUnix: 0, lastError: '' });
      serviceContainer.analyticsManager.sendRestartScheduleExpiredEvent();
      return;
    }
    this.expiryTimer = setTimeout(() => {
      localStorage.removeItem(STORAGE_KEY);
      this.store.updateState({ scheduled: false, scheduledTimeUnix: 0, lastError: '' });
      serviceContainer.analyticsManager.sendRestartScheduleExpiredEvent();
    }, ms);
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }
}
